import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';

/* The one scroll-reveal primitive of the ng design system. The house curve is
   [0.22, 1, 0.36, 1] — deliberately NOT the lp3 curve, so the two markets do
   not move alike. Slow, confident, once. */

const NG_EASE = [0.22, 1, 0.36, 1] as const;

interface NgRevealProps {
    children: React.ReactNode;
    className?: string;
    delay?: number;
    /* 'rise' fades up 28px (default). 'mask' rises out of an overflow-hidden
       sleeve — for display headlines. 'rule' is for hairlines: scaleX 0→1. */
    variant?: 'rise' | 'mask' | 'rule';
}

const NgReveal: React.FC<NgRevealProps> = ({
    children,
    className,
    delay = 0,
    variant = 'rise',
}) => {
    const reduced = useReducedMotion();

    if (variant === 'mask') {
        /* Mask reveals are used in heroes, which are in view at load — so they
           play on mount rather than on intersection. An observer here can miss
           or freeze when the element is already intersecting at hydration. */
        return (
            <div style={{ overflow: 'hidden' }} className={className}>
                <motion.div
                    initial={reduced ? false : { y: '104%' }}
                    animate={{ y: '0%' }}
                    transition={{ duration: 1, ease: NG_EASE, delay }}
                >
                    {children}
                </motion.div>
            </div>
        );
    }

    if (variant === 'rule') {
        return (
            <motion.div
                className={className}
                initial={reduced ? false : { scaleX: 0 }}
                whileInView={{ scaleX: 1 }}
                viewport={{ once: true, margin: '-40px' }}
                transition={{ duration: 1.1, ease: NG_EASE, delay }}
                style={{ transformOrigin: 'left center' }}
            >
                {children}
            </motion.div>
        );
    }

    return (
        <motion.div
            className={className}
            initial={reduced ? false : { opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-60px' }}
            transition={{ duration: 0.9, ease: NG_EASE, delay }}
        >
            {children}
        </motion.div>
    );
};

export default NgReveal;
